import { ApolloError } from '@apollo/client';
import { Box, PlacementWithLogical } from '@chakra-ui/react';
import { ColumnFilterPopover, DataTable } from '@gamma/ui/data-table';
import { FlushPanelContent, Panel } from '@gamma/ui/layout';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Accessor, Column } from 'react-table';
import { ROUTES, graphqlErrorRedirects } from '@investigator/shared/constants';
import {
  useDateRangeQueryString,
  useIsNewSearch,
  useManualPagination,
  useUserSession,
} from '@investigator/shared/hooks';
import { i18n } from '@investigator/shared/localization';
import { OrgTenantsContext } from '@investigator/shared/providers';
import { escapeSpecialChars } from '@investigator/shared/utils/escapeSpecialChars';
import {
  EntityAnalysisPopover,
  TopDetectionEntitiesTablePopper,
} from '../../../entities/components';
import { useGetTopDetectionEntitiesPaginated } from '../../queries';

import validator from 'validator';
import { TopAlertsTableFilters } from '../AlertCategoriesDetectionsTable';
import { DetectionSummary } from '@investigator/shared/generated/graphql';
import { SeverityScore } from '@investigator/shared/components';

interface HighRiskEntitiesDetectionsTableProps {
  hiddenCols?: (Accessor<DetectionSummary> | string)[];
  rowPopperPlacement?: PlacementWithLogical;
  onDataLoad?: (data: {
    summaries: {
      severity: number;
      entity_name: string;
      detection_count: number;
      entity_category: string;
      entity_type: string;
      total_detections: number;
      is_custom_severity: boolean;
    }[];
    total_items: number;
  }) => void;
}

const createDataTableColumns = (
  start: number,
  end: number,
): Column<DetectionSummary>[] => [
  {
    width: 100,
    Header: i18n.tables.score,
    accessor: 'severity',
    align: 'center',
    Cell: (props) => {
      return <SeverityScore score={props.value ?? 0} />;
    },
  },
  {
    width: 200,
    Header: i18n.tables.entity,
    accessor: 'entity_name',
    disableSortBy: true,
    Filter: ColumnFilterPopover,
    Cell: (props) =>
      !validator.isIP(props.value ?? '') ? (
        <Box ml={2}>{props.value}</Box>
      ) : (
        <EntityAnalysisPopover
          detectionSummary={props.row.original}
          requireSingleDetectionForSuppression={true}
          start={start}
          end={end}
        />
      ),
  },
  {
    Header: i18n.tables.detectionsHeading,
    accessor: 'detection_count',
  },
];

const mustCondition = (
  filters: { id: string; value: string }[],
  orgTenantsQueryParam?: string[],
) => {
  const formattedQuery = escapeSpecialChars({
    chars: filters?.[0]?.value,
  });

  return [
    `{"regexp":{"alert_entity.entity_name.keyword":{"value":".*${formattedQuery}.*","case_insensitive":true}}}`,
    `{"terms": {"tenant": ${JSON.stringify(orgTenantsQueryParam)}}}`,
    '{"term": {"detection_status": "open"}}',
  ];
};

export const HighRiskEntitiesDetectionsTableFooter = ({
  loading,
  error,
  data,
  signOut,
}: {
  loading: boolean;
  error: ApolloError | undefined;
  data: DetectionSummary[];
  signOut: () => Promise<void>;
}) => {
  if (loading || error) {
    return (
      <Panel>
        <GraphQLReqStatus
          loading={loading}
          error={error}
          signOut={signOut}
          extended={graphqlErrorRedirects}
        />
      </Panel>
    );
  }
  if (!data.length) {
    return <Panel>{i18n.pages.alerts.na}</Panel>;
  } else {
    return null;
  }
};

export const HighRiskEntitiesDetectionsTable = (
  props: HighRiskEntitiesDetectionsTableProps,
) => {
  const { signOut } = useUserSession();
  const { orgTenantsQueryParam } = useContext(OrgTenantsContext);

  const {
    hiddenCols = [],
    rowPopperPlacement = 'right-start',
    onDataLoad,
  } = props;
  const navigate = useNavigate();

  const { start, end, sevenDayStart, sevenDayEnd } = useDateRangeQueryString();

  // Use URL params if valid, otherwise fallback to stable 7-day values from hook
  const validStart = start && !isNaN(start) ? start : sevenDayStart;
  const validEnd = end && !isNaN(end) ? end : sevenDayEnd;

  const [initialFetch, setInitialFetch] = useState<boolean>(false);
  const [topEntitiesTableData, setTopEntitiesTableData] = useState<
    DetectionSummary[]
  >([]);
  const [filters, setFilters] = useState<
    { id: string; value: string }[] | null
  >(null);

  // Memoize initial sort to prevent render loop
  const initialSortBy = useMemo(() => [{ id: 'severity', desc: true }], []);

  const {
    sortBy,
    offset,
    pageSize,
    pageIndex,
    pageCount,
    onFetchData,
    getPageCount,
    getSortsForAPI,
  } = useManualPagination<DetectionSummary>({
    initialFetch,
    sortBy: initialSortBy,
  });

  useEffect(() => {
    setInitialFetch(true);
  }, [validStart, validEnd]);

  const filteredDataTableColumns = useMemo(
    () =>
      createDataTableColumns(validStart, validEnd).filter(
        (col) => col.accessor && !hiddenCols.includes(col.accessor),
      ),
    [hiddenCols, validStart, validEnd],
  );

  const { isNewSearch } = useIsNewSearch({
    filters,
  });

  const noFilterVariables = {
    start: validStart,
    end: validEnd,
    offset: isNewSearch ? 0 : offset,
    size: pageSize,
    sort: getSortsForAPI(sortBy),
    must_conds: [
      `{"terms": {"tenant": ${JSON.stringify(orgTenantsQueryParam)}}}`,
      '{"term": {"detection_status": "open"}}',
    ],
  };

  const variables = filters?.length
    ? {
        ...noFilterVariables,
        must_conds: mustCondition(filters, orgTenantsQueryParam),
      }
    : noFilterVariables;

  const { data, loading, error, refetch } = useGetTopDetectionEntitiesPaginated(
    {
      notifyOnNetworkStatusChange: true,
      skip: !validStart || !validEnd || !orgTenantsQueryParam?.length,
      variables,
      onCompleted: (data: {
        getTopDetectionEntitiesPaginated: {
          summaries: DetectionSummary[];
          total_items: number;
        };
      }) => {
        setInitialFetch(false);
        getPageCount(data.getTopDetectionEntitiesPaginated);
        setTopEntitiesTableData(
          data.getTopDetectionEntitiesPaginated.summaries,
        );
      },
    },
  );

  useEffect(() => {
    if (data?.getTopDetectionEntitiesPaginated && !loading && onDataLoad) {
      onDataLoad({
        summaries: data.getTopDetectionEntitiesPaginated.summaries.map(
          (summary) => ({
            severity: summary.severity ?? 0,
            entity_name: summary.entity_name ?? '',
            detection_count: summary.detection_count ?? 0,
            entity_category: summary.entity_category ?? 'source',
            entity_type: summary.entity_type ?? '',
            total_detections: summary.total_detections ?? 0,
            is_custom_severity: summary.is_custom_severity ?? false,
          }),
        ),
        total_items: data.getTopDetectionEntitiesPaginated.total_items,
      });
    }
  }, [data, loading, onDataLoad]);

  const handleColFilter = (filters: TopAlertsTableFilters) => {
    setFilters(filters ?? null);
    if (filters.length) {
      refetch({
        ...noFilterVariables,
        offset: 0,
        must_conds: mustCondition(filters, orgTenantsQueryParam),
      });
    }
  };

  const tableData =
    !loading && !error && topEntitiesTableData.length
      ? topEntitiesTableData
      : [];

  return (
    <Panel>
      <FlushPanelContent>
        <div data-testid="top-entities-table">
          <DataTable
            isMemod={true}
            hideColumnHeaders={loading}
            isPaginable={Boolean(!loading && !error && tableData.length)}
            isFilterManual={true}
            onColumnFilter={handleColFilter}
            data={tableData}
            pageSize={pageSize}
            pageCount={pageCount}
            autoResetPage={false}
            autoResetSortBy={false}
            isPaginationManual={true}
            columns={filteredDataTableColumns}
            onFetchData={onFetchData}
            rowWrapper={TopDetectionEntitiesTablePopper(rowPopperPlacement)}
            itemCount={data?.getTopDetectionEntitiesPaginated?.total_items || 0}
            onRowClick={({ original }, event) => {
              if (
                (event.target as HTMLElement)?.className?.includes &&
                ![
                  'icon',
                  'link',
                  'menu',
                  'button',
                  'switch',
                  'text',
                  'image',
                  'stack',
                ].some((classNameString) =>
                  (event.target as HTMLElement).className.includes(
                    classNameString,
                  ),
                )
              ) {
                navigate({
                  pathname: ROUTES.detections,
                  search: `start=${validStart}&end=${validEnd}&${
                    original.entity_category ?? 'source'
                  }_name=${original.entity_name}`,
                });
              }
            }}
            initialState={{
              pageIndex,
              sortBy,
            }}
            footer={HighRiskEntitiesDetectionsTableFooter({
              loading,
              error,
              data: topEntitiesTableData,
              signOut,
            })}
          />
        </div>
      </FlushPanelContent>
    </Panel>
  );
};
